import { CommonResponse } from '@models/common';
import { ApiError, ApiErrorClass } from '@utils/customErrors';
import { handleClientApiError, throwClientFailError } from './errors';
import { fetchWithTimeout } from './timeout';
import { applyDisplayHeaders } from './headers';
import { createRequestLogger } from './logger';
import type { FetchParams } from './types';

const isSsr = (): boolean => typeof window === 'undefined';

export async function commonFetch<
  IResponseData = undefined,
  IRequestParams = undefined
>({
  url,
  params,
  options,
  baseUrl,
  responseType = 'json',
}: FetchParams<IRequestParams>): Promise<IResponseData> {
  const fullUrl = `${baseUrl || ''}${url}`;
  const requestId = new Date().toISOString();
  const { collectLog, printLogs } = createRequestLogger(fullUrl, requestId);

  try {
    collectLog('API 요청 시작', 'request');
    collectLog(`요청 URL: ${fullUrl}`, 'info');
    collectLog('옵션:', 'info');
    collectLog(`${JSON.stringify(options)}`, 'info');

    const tempOptions = { ...options };
    let tempUrl = fullUrl;
    if (options.method === 'GET' && params) {
      tempUrl += `?${new URLSearchParams(params as string).toString()}`;
    } else if (params instanceof FormData) {
      tempOptions.body = params;
    } else {
      tempOptions.body = JSON.stringify(params);
    }

    await applyDisplayHeaders({ tempOptions, options, baseUrl });

    collectLog('변경된 옵션:', 'info');
    collectLog(`${JSON.stringify(tempOptions)}`, 'info');

    const startTime = performance.now();
    console.log('API 요청 시작:', {
      url,
      timestamp: new Date().toISOString(),
      startTime,
    });
    const response = await fetchWithTimeout(tempUrl, {
      ...tempOptions,
      cache: options.ssg ? undefined : 'no-store',
    });
    const endTime = performance.now();
    const duration = endTime - startTime;
    console.log('API 요청 완료:', {
      url,
      timestamp: new Date().toISOString(),
      duration: `${duration.toFixed(2)}ms`,
      status: response.status,
    });

    if (responseType === 'raw') {
      return response as IResponseData;
    }

    if (!response.ok) {
      const errorData = (await response
        .json()
        .catch(() => null)) as Partial<ApiError> | null;
      throw new ApiErrorClass(
        '서버 에러',
        response.status || 500,
        errorData ?? {}
      );
    }

    if (responseType === 'blob') {
      const blob = await response.blob();
      return blob as IResponseData;
    }

    const data = (await response.json()) as CommonResponse;

    collectLog('응답 데이터:', 'response');
    collectLog(`${JSON.stringify(data)}`, 'response');

    if (data?.result === 'FAIL') {
      if (!isSsr()) {
        throwClientFailError(data);
      }
      return data as IResponseData;
    }
    return data as IResponseData;
  } catch (error) {
    collectLog('API 에러 발생', 'error');
    collectLog(`Fetch Error: ${fullUrl}, ${error}`, 'error');
    return handleClientApiError<IResponseData>(error as ApiError);
  } finally {
    printLogs();
  }
}
